import * as React from 'react';
import './Node.css';
import * as _ from 'lodash'

import { DropTarget, DragSource, DropTargetConnector, DropTargetMonitor, DropTargetSpec, DragSourceConnector, DragSourceMonitor, DragSourceSpec } from 'react-dnd'

import NodeBox from './NodeBox'
import NodeValue from './NodeValue'
import EditableText from './EditableText'
import WithContextMenu, { Menu } from './WithContextMenu'
import MyCodeMirror from './MyCodeMirror'
import CallCodeEditor from './CallCodeEditor'
import * as actions from '../actions'

import copy from 'clipboard-copy'
import stringify from '../stringify'

import { keys } from 'src/lib/keys'
import classNames from 'classnames'

import { nodeInfo, referredOrOwnNodeId, NodeInfo, GraphNodeInfo } from '../model/nodeInfo'
import { NetworkUser } from 'golgi'
import { evaluateNode, lastNodeIdInStepsC } from '../model/evaluate'
import { network, docC, documentC, docCheck, debugMode } from '../globals'
import { NodeDrag, DefinitionDrag } from 'src/view/drags';
import { Cursor, CursorV } from 'src/lib/Cursor';
import { Stack, canDragFromStackIntoStack } from 'src/types/stack';
import * as Model from 'src/types/document'
import { standardSCU, clog } from 'src/util';
import Sizer, { Size } from 'src/view/Sizer';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { findDOMNode } from 'react-dom';
import { number } from 'prop-types';
import { nodeIsActive } from 'src/model/nodeIsActive';
import { Result } from 'src/types/result';
import { HoveredNodeConsumer, HoveredNodeState, HoveredNodeContext } from 'src/view/HoveredNodeContext';
import { Prop, withContext } from 'src/view/withContext';

interface NodeProps {
  nodeId: string,
  blockC: Cursor<Model.Block>,
  inlineCallToEditC?: Cursor<Model.CodeCall>,
  stack: Stack,

  zoom?: number,
  activeIndex?: number | string,
  setActiveIndex?: (activeIndex: number | string) => void,
  
  subtreeComponent?: React.Component,

  onMouseOver?: () => void,
  onMouseOut?: () => void,
  onClick?: () => void,
}

const dragSpec: DragSourceSpec<NodeProps, NodeDrag> = {
  beginDrag(props, _monitor, component) {
    console.log("beginDrag", component)
    return network.sample((): NodeDrag => {
      const {nodeId, blockC, stack} = props
      const info = nodeInfo(blockC, nodeId)
      const type = info.type !== 'graph' ? 'copy-reference' : keys.altKey ? 'copy-reference': 'move-subtree'
      const nodeDomElement = (findDOMNode(component) || undefined) as (Element | undefined)
      let displayDomElement = undefined as Element | undefined
      let domElementOffset = undefined as {x: number, y: number} | undefined
      if (type === 'move-subtree' && props.subtreeComponent) {
        displayDomElement = (findDOMNode(props.subtreeComponent) || undefined) as (Element | undefined)
        if (displayDomElement && nodeDomElement) {
          const nodeRect = nodeDomElement.getBoundingClientRect()
          const displayRect = displayDomElement.getBoundingClientRect()
          domElementOffset = {x: displayRect.left - nodeRect.left, y: displayRect.top - nodeRect.top}
        }
      } else {
        displayDomElement = nodeDomElement
        domElementOffset = {x: 0, y: 0}
      }
      console.log("drag", {
        domElement: displayDomElement,
        domElementOffset,
        component
      })
      return {
        type,
        nodeId,
        blockC,
        stack,
        domElement: displayDomElement,
        domElementOffset,
      }
    })
  },

  // canDrag() {
  //   return false
  // }
};

function dragCollect(connect: DragSourceConnector, monitor: DragSourceMonitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  }
}

const dropSpec: DropTargetSpec<NodeProps> = {
  drop(props, monitor) { actions.act(() => {
    switch (monitor.getItemType()) {
    case 'node':
      const nodeDrag = monitor.getItem() as NodeDrag
      const info = nodeInfo(props.blockC, props.nodeId) as GraphNodeInfo  // TODO: make sure we are only dropping things onto graph nodes!
      const graphC = NodeInfo.graphC(info)
      switch (nodeDrag.type) {
        case 'move-subtree':
          actions.moveSubtree(nodeDrag.blockC, nodeDrag.nodeId, graphC)
          if (info.usingCallId) {
            actions.replaceInput(graphC._('calls')._(info.usingCallId), props.nodeId, nodeDrag.nodeId)
          } else {
            actions.deleteSubtree(props.blockC, props.nodeId)
          }
          break
        case 'copy-reference':
          const newRefNodeId = actions.addReferenceNodeToGraph(referredOrOwnNodeId(nodeDrag.blockC, nodeDrag.nodeId), NodeInfo.graphC(info), props.blockC)
          if (info.usingCallId) {
            actions.replaceInput(graphC._('calls')._(info.usingCallId), props.nodeId, newRefNodeId)
          } else {
            actions.deleteSubtree(props.blockC, props.nodeId)
          }
          break
        default:
          throw new Error(`tried to drop unknown cell-type ${nodeDrag.type} on StepSpacer`)
      }
      break
    case 'definition':
      const definitionDrag = monitor.getItem() as DefinitionDrag
      actions.addDefinitionCallFrom(props.blockC, props.nodeId, definitionDrag.definitionId)
      break
    default:
      throw new Error(`tried to drop unknown type ${JSON.stringify(monitor.getItemType())} on StepDropSite`)
    }
  })},

  canDrop(props, monitor) {
    switch (monitor.getItemType()) {
      case 'node':
        const dragged = monitor.getItem() as NodeDrag
        return canDragFromStackIntoStack(dragged.stack, props.stack) && dragged.nodeId !== props.nodeId
      case 'definition':
        return true
    }
    return false
  }
}

function dropCollect(connect: DropTargetConnector, monitor: DropTargetMonitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    canDrop: monitor.canDrop(),
    isOver: monitor.isOver(),
  }
}

class Node extends React.Component<NodeProps & ReturnType<typeof dropCollect> & ReturnType<typeof dragCollect> & Prop<'hoveredNode', HoveredNodeContext>> {
  state = {
    inlineCallSize: undefined as Size | undefined,
  }

  render() {
    // console.log("NODE OUTER RENDER")
    return <NetworkUser debugName="Node" props={this.props} state={this.state} render={() => {
      // console.log("NODE INNER RENDER")
      const {blockC, nodeId, inlineCallToEditC, stack, activeIndex, setActiveIndex, zoom, onMouseOver, onMouseOut, onClick} = this.props
      const {connectDragSource, isDragging} = this.props
      const {connectDropTarget, canDrop, isOver} = this.props
      const {hoveredNode} = this.props
      const {inlineCallSize} = this.state

      const info = nodeInfo(blockC, nodeId)
      const nodeC = NodeInfo.nodeC(info)
      const node = nodeC.get()
      
      const active = nodeIsActive(blockC, nodeId, stack)

      const result: Result<any> = active ? evaluateNode(node.id, stack) : {type: 'value', value: undefined}

      let boxWidth = 140
      let boxHeight = 140
      let zoomToFit = !(result.type === 'value' && result.value && result.value.isDiagram)

      const isOutput = nodeId === lastNodeIdInStepsC(blockC._('steps'))

      const isError = result instanceof Error

      let boxBackground = canDrop ? (isOver ? "#ded" : "#eee") : undefined

      if (hoveredNode.is && hoveredNode.is(nodeId, stack)) {
        boxBackground = "#BFF2E6" // "#66E0C2"
      }

      if (result === undefined ||
          result.type === 'error' ||
          result.value === undefined ||
          typeof(result.value) === 'number' ||
          typeof(result.value) === 'boolean') {
        boxWidth = 70
        boxHeight = 70
      }

      // if (result && result.value && JSON.stringify(result.value).length >= 280) {
      //   boxWidth = 350
      //   boxHeight = 350
      //   zoomToFit = false
      // }

      // console.log("rendering node, document is", this.props.document.definitions[this.props.document.definitions.length - 1])

      const inlineCallToEdit = inlineCallToEditC && inlineCallToEditC.get()
      const isEmpty =
        (inlineCallToEdit && inlineCallToEdit.callType === 'code' && !inlineCallToEdit.code.length)
        || (info.type === 'input' && (node as Model.DefinitionInput).settings[(node as Model.DefinitionInput).activeSetting].length == 0)

      let rendered = connectDragSource(
        <div className={classNames("Node", {"Node-error": isError,
                                            "Node-empty": isEmpty,
                                            "Node-dragged": isDragging})}>
          <div className="Node-inside" style={zoom ? {zoom} : {}}>
            <EditableText className="Node-caption"
              value={node.name || ""}
              setValue={newValue => nodeC.select('name').set(newValue) } />
            {/* <div style={{display: "flex"}}> */}
              <NodeBox isOutput={isOutput} zoomToFit={zoomToFit} background={boxBackground}  // TODO: oh noooooo
                       width={boxWidth} height={boxHeight} minWidth={inlineCallSize && inlineCallSize.width}
                       divProps={{
                        onMouseOver: () => actions.act(() => {
                          onMouseOver && onMouseOver()
                          nodeId && blockC && hoveredNode.set && hoveredNode.set(nodeId, stack)
                        }),
                        onMouseOut: () => actions.act(() => {
                          onMouseOut && onMouseOut()
                          nodeId && blockC && hoveredNode.clear && hoveredNode.clear()
                        }),
                        onClick: () => actions.act(() => {
                          onClick && onClick()
                        }),
                       }}>
                <NodeValue blockC={blockC} nodeId={nodeId} stack={stack}
                           result={result} width={boxWidth} height={boxHeight}
                           activeIndex={activeIndex} setActiveIndex={setActiveIndex}/>
                { debugMode &&
                  <div style={{fontSize: "70%"}}>
                    {nodeC.get().id}
                  </div>
                }
              </NodeBox>
            {/* </div> */}
            <div className='Node-bottom'>
              { inlineCallToEditC &&
                <WithContextMenu menuMaker={() => [
                  {type: 'heading', heading: "CODE"},
                  {type: 'contents', contents: "Expand",
                    onClick: () => actions.act(() => {
                      const { blockC } = this.props
                      const graphC = NodeInfo.graphC(nodeInfo(blockC, nodeId) as GraphNodeInfo)
                      actions.expandCall(inlineCallToEditC, graphC, blockC)
                    })},
                ]}>
                  <Sizer owner={this} stateName="inlineCallSize">
                    <CallCodeEditor callC={inlineCallToEditC} graphC={NodeInfo.graphC(nodeInfo(blockC, nodeId) as GraphNodeInfo)} blockC={blockC} stack={stack} />
                  </Sizer>
                </WithContextMenu>
              }
            </div>
          </div>
        </div>
      );

      if (active) {
        rendered = connectDropTarget(rendered)
        
        rendered = (
          <WithContextMenu menuMaker={this.menuMaker}>
            {rendered}
          </WithContextMenu>
        )
      }

      return rendered
    }} />
  }

  menuMaker = () => {
    return network.sample(() => {
      const {nodeId, blockC, stack} = this.props

      let menu: Menu = []

      const result = evaluateNode(nodeId, stack)

      menu.push({
        type: 'heading',
        heading: "NODE",
      }, {
        type: 'contents',
        contents: <React.Fragment>Run <span style={{fontFamily: "monospace"}}>code</span></React.Fragment>,
        onClick: () => actions.act(() => actions.addCodeCallFrom(blockC, nodeId))
      })

      if (result.type === 'value' && result.value &&
          (result.value.forEach || result.value instanceof Object)) {
        menu.push({
          type: 'contents',
          contents: <React.Fragment>Run <span className="font-definition">map</span></React.Fragment>,
          onClick: () => actions.act(() => actions.addListOpCallFrom(blockC, nodeId, 'map'))
        }, {
          type: 'contents',
          contents: <React.Fragment>Run <span className="font-definition">filter</span></React.Fragment>,
          onClick: () => actions.act(() => actions.addListOpCallFrom(blockC, nodeId, 'filter'))
        })
      }

      documentC.get().definitions.forEach((definition) =>
        menu.push({
          type: 'contents',
          contents: <React.Fragment>Run <span className="font-definition">{definition.name}</span></React.Fragment>,
          onClick: () => actions.act(() => actions.addDefinitionCallFrom(blockC, nodeId, definition.id)),
        })
      )

      if (result.type === 'value' && result.value) {
        menu.push({
          type: 'divider'
        }, {
          type: 'contents',
          contents: <React.Fragment>Copy value</React.Fragment>,
          onClick: () => {
            copy(stringify(result.value, {maxLength: 20}))
          }
        })
      }

      return menu
    })
  }

  public componentDidMount() {
		const { connectDragPreview } = this.props
    connectDragPreview(getEmptyImage(), {
      // IE fallback: specify that we'd rather screenshot the node
      // when it already knows it's being dragged so we can hide it with CSS.
      captureDraggingState: true,
    })
  }
}

// export default Value
export default (
  withContext<HoveredNodeContext, 'hoveredNode', NodeProps>('hoveredNode', HoveredNodeConsumer)(
    DropTarget(["node", "definition"], dropSpec, dropCollect, {arePropsEqual: () => false})(
      DragSource("node", dragSpec, dragCollect, {arePropsEqual: () => false})(
          Node))))



// WEBPACK FOOTER //
// ./src/view/Node.tsx